const matchers = {
    //Types
    undefined: (value) => {
        return value === undefined;
    },
    null: (value) => {
        return value === null;
    },
    false: (value) => {
        return value === false;
    },
    true: (value) => {
        return value === true;
    },
    falsy: (value) => {
        return !value;
    },
    truthy: (value) => {
        return !!value;
    },
    number: (value) => {
        return typeof value === "number" && !isNaN(value);
    },
    boolean: (value) => {
        return value === true || value === false;
    },
    string: (value) => {
        return typeof value === "string";
    },
    function: (fn) => {
        return typeof fn === "function";
    },
    object: (value) => {
        return Object.prototype.toString.call(value) === "[object Object]";
    },
    date: (value) => {
        return (
            Object.prototype.toString.call(value) === "[object Date]" &&
            typeof value.getDate === "function" &&
            !isNaN(value.getDate())
        );
    },
    array: (value) => {
        return Array.isArray(value);
    },
    regexp: (value) => {
        return (
            typeof value === "object" && Object.prototype.toString.call(value) === "[object RegExp]"
        );
    },
    minChars: (value) => {
        return matchers.match(`^.{${value},}$`);
    },
    htmlElement: (element) => {
        return matchers.or(matchers.instanceof(HTMLElement), matchers.instanceof(Element))(element);
    },
    //Math
    /**
     * Return if target > expect
     * @param {Number} expect
     * @returns {Boolean}
     */
    gt: (expect) => {
        return (target) => {
            if (!matchers.number(expect) || !matchers.number(target)) {
                return false;
            }
            return target > expect;
        };
    },
    /**
     * Return if target >= expect
     * @param {Number} expect
     * @returns {Boolean}
     */
    gte: (expect) => {
        return (target) => {
            if (!matchers.number(expect) || !matchers.number(target)) {
                return false;
            }
            return target >= expect;
        };
    },
    /**
     * Return if target < expect
     * @param {Number} expect
     * @returns {Boolean}
     */
    lt: (expect) => {
        return (target) => {
            if (!matchers.number(expect) || !matchers.number(target)) {
                return false;
            }
            return target < expect;
        };
    },
    /**
     * Return if target <= expect
     * @param {Number} expect
     * @returns {Boolean}
     */
    lte: (expect) => {
        return (target) => {
            if (!matchers.number(expect) || !matchers.number(target)) {
                return false;
            }
            return target <= expect;
        };
    },
    // Logical
    /**
     * Test if target === expected
     * @param {*} expect
     * @returns {Boolean}
     */
    eq: (expect) => {
        return (target) => {
            return target === expect;
        };
    },
    /**
     * Test if target !== expected
     * @param {*} expect
     * @returns {Boolean}
     */
    neq: (expect) => {
        return (target) => {
            return target !== expect;
        };
    },
    /**
     * Return if **value** is in btw startRange and endRange
     * @param {Number} startRange
     * @param {Number} endRange
     * @returns
     */
    inRange: (startRange, endRange) => {
        return (value) => {
            if (
                !matchers.number(startRange) ||
                !matchers.number(endRange) ||
                !matchers.number(value)
            ) {
                return false;
            }
            return value >= startRange && value <= endRange;
        };
    },
    /**
     * All arguments must be a function.
     * They will called with **value**
     * All functions must return true to pass.
     * @param  {...any} args list of functions
     * @returns {Boolean}
     */
    and: (...args) => {
        return (value) => {
            if (!args.length) return false;
            return args.reduce((result, fn) => {
                return (result = result && matchers.function(fn) && fn(value));
            }, true);
        };
    },
    /**
     * All arguments must be a function.
     * They will called with **value**
     * At least one of the call must return true to pass
     * @param  {...any} args list of functions
     * @returns {Boolean}
     */
    or: (...args) => {
        return (value) => {
            if (!args.length) return false;
            return args.reduce((result, fn) => {
                return (result = result || (matchers.function(fn) && fn(value)));
            }, false);
        };
    },
    /**
     * Test if value matches pattern
     * @param {String|RegExp} pattern
     * @returns {Boolean}
     */
    match: (pattern) => {
        return (value) => {
            const matchingExpectedTypes = matchers.or(matchers.string, matchers.regexp)(pattern);
            return matchingExpectedTypes && matchers.string(value) && !!value.match(pattern);
        };
    },
    /**
     * Test if all values from **list** exists in **refs**
     * refs:[a,b,c] list:[a,b] => true
     * refs:[a,b,c] list: [a,c] =>  false
     * refs:[a,b,c] list: [c] =>  false
     * @param {Array} refs list with expected values
     * @returns Boolean
     */
    includesList: (refs) => {
        return (list) => {
            if (!matchers.array(refs) || !matchers.array(list)) {
                return false;
            }
            const itemsMatching = list
                .map((value) => {
                    return refs.includes(value);
                })
                .filter((pass) => {
                    return !!pass;
                });
            return itemsMatching.length === list.length;
        };
    },
    /**
     * Test if **value** exists in refs (Uses Array includes)
     * @param {Array} refs
     * @returns {Boolean}
     */
    includes: (refs) => {
        return (value) => {
            return matchers.array(refs) && refs.includes(value);
        };
    },
    /**
     * Return if value is instanceof Instance
     * @param {*} Instance
     * @returns {Boolean}
     */
    instanceof: (Instance) => {
        return (value) => {
            return value instanceof Instance;
        };
    },
};

export default matchers;
